import { RecentlyViewed } from "@theme/recently-viewed-products";

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: "0px 0px 400px 0px" }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== "attributes") continue;

      // Ignore error attribute changes
      if (mutation.attributeName === "data-error") continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === "class" && this.classList.contains("hidden")) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (mutation.attributeName === "data-recommendations-performed" && this.dataset.recommendationsPerformed === "true") continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Refs object to store DOM element references
   * @type {Record<string, HTMLElement | HTMLElement[]>}
   */
  refs = {};

  /**
   * Currently active tab name
   * @type {string | null}
   */
  #activeTab = null;

  /**
   * The main section heading element whose text updates with the active tab
   * @type {HTMLElement | null}
   */
  #sectionHeading = null;

  /**
   * Bound tab click handlers for proper cleanup
   */
  #tabClickHandlers = {
    recommendations: null,
    recentlyViewed: null,
  };

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    // Initialize tabs if recently viewed is enabled
    if (this.dataset.enableRecentlyViewed === "true") {
      // Wait for DOM to be ready, then initialize tabs
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          this.#initializeRefs();
          this.#initializeTabs();
        });
      } else {
        this.#initializeRefs();
        this.#initializeTabs();
      }
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#removeTabListeners();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error("Product ID and an ID attribute are required");
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === "true") {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement("div");
        html.innerHTML = result.data || "";
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = "true";
          this.innerHTML = recommendations.innerHTML;

          // Reinitialize refs and tabs after HTML is loaded
          if (this.dataset.enableRecentlyViewed === "true") {
            this.#initializeRefs();
            this.#initializeTabs();
          }
        } else {
          this.#handleError(new Error("No recommendations available"));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Product recommendations error:", error.message);
    this.classList.add("hidden");
    this.dataset.error = "Error loading product recommendations";
  }

  /**
   * Initialize refs for DOM elements
   */
  #initializeRefs() {
    const refElements = this.querySelectorAll("[ref]");
    refElements.forEach((element) => {
      const refName = element.getAttribute("ref");
      if (refName) {
        // Handle array refs (ref="name[]")
        if (refName.endsWith("[]")) {
          const key = refName.slice(0, -2);
          if (!this.refs[key]) {
            this.refs[key] = [];
          }
          this.refs[key].push(element);
        } else {
          this.refs[refName] = element;
        }
      }
    });
  }

  /**
   * Initialize tab functionality
   */
  #initializeTabs() {
    if (this.dataset.showRecentlyViewedOnly === "true") {
      // Show only recently viewed, no tabs needed
      return;
    }

    // check if the recently viewed is having products
    const recentlyViewedProducts = RecentlyViewed.getProducts();
    if (recentlyViewedProducts.length <= 1) {
      this.refs.recentlyViewedTab.classList.add("hidden");
    } else {
      this.refs.recentlyViewedTab.classList.remove("hidden");
    }

    const recommendationsTab = this.refs.recommendationsTab;
    const recentlyViewedTab = this.refs.recentlyViewedTab;

    // Store the original heading element for tab-driven updates
    this.#sectionHeading = this.refs.sectionContent?.querySelector("h1, h2, h3, h4, p") || null;

    if (recommendationsTab && recentlyViewedTab) {
      // Set initial active tab
      this.#activeTab = "recommendations";

      // Create bound handlers
      this.#tabClickHandlers.recommendations = () => this.#switchTab("recommendations");
      this.#tabClickHandlers.recentlyViewed = () => this.#switchTab("recently-viewed");

      // Add click event listeners
      recommendationsTab.addEventListener("click", this.#tabClickHandlers.recommendations);
      recentlyViewedTab.addEventListener("click", this.#tabClickHandlers.recentlyViewed);
    }
  }

  /**
   * Remove tab event listeners
   */
  #removeTabListeners() {
    const recommendationsTab = this.refs.recommendationsTab;
    const recentlyViewedTab = this.refs.recentlyViewedTab;

    if (recommendationsTab && this.#tabClickHandlers.recommendations) {
      recommendationsTab.removeEventListener("click", this.#tabClickHandlers.recommendations);
    }
    if (recentlyViewedTab && this.#tabClickHandlers.recentlyViewed) {
      recentlyViewedTab.removeEventListener("click", this.#tabClickHandlers.recentlyViewed);
    }
  }

  /**
   * Switch between tabs
   * @param {string} tabName - Name of the tab to switch to ('recommendations' or 'recently-viewed')
   */
  #switchTab(tabName) {
    if (this.#activeTab === tabName) return;

    this.#activeTab = tabName;

    // Update tab buttons
    const recommendationsTab = this.refs.recommendationsTab;
    const recentlyViewedTab = this.refs.recentlyViewedTab;

    if (recommendationsTab && recentlyViewedTab) {
      if (tabName === "recommendations") {
        recommendationsTab.classList.add("product-recommendations__tab--active");
        recommendationsTab.setAttribute("aria-selected", "true");
        recentlyViewedTab.classList.remove("product-recommendations__tab--active");
        recentlyViewedTab.setAttribute("aria-selected", "false");
      } else {
        recentlyViewedTab.classList.add("product-recommendations__tab--active");
        recentlyViewedTab.setAttribute("aria-selected", "true");
        recommendationsTab.classList.remove("product-recommendations__tab--active");
        recommendationsTab.setAttribute("aria-selected", "false");
      }
    }

    // Update main heading text to reflect active tab
    if (this.#sectionHeading) {
      const activeTabEl = tabName === "recommendations" ? recommendationsTab : recentlyViewedTab;
      if (activeTabEl) this.#sectionHeading.textContent = activeTabEl.textContent.trim();
    }

    // Update panels
    const recommendationsPanel = this.refs.recommendationsPanel;
    const recentlyViewedPanel = this.refs.recentlyViewedPanel;

    if (recommendationsPanel && recentlyViewedPanel) {
      if (tabName === "recommendations") {
        recommendationsPanel.classList.add("product-recommendations__panel--active");
        recommendationsPanel.removeAttribute("inert");
        recentlyViewedPanel.classList.remove("product-recommendations__panel--active");
        recentlyViewedPanel.setAttribute("inert", "");
      } else {
        recentlyViewedPanel.classList.add("product-recommendations__panel--active");
        recentlyViewedPanel.removeAttribute("inert");
        recommendationsPanel.classList.remove("product-recommendations__panel--active");
        recommendationsPanel.setAttribute("inert", "");
      }
    }
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
